import {
  DISCOVERY_MIN_SCORE,
  dbFields,
  defaultListColumnKeys,
  fieldDefinitions,
  hiddenStatuses,
  inactiveStatuses,
  integerFields,
  jsonFields,
  listColumns,
  nullableFields,
  personFields,
  priorityOrder,
  priorityScore,
  statusOrder,
} from "./constants";
import type { CSSProperties } from "react";
import type {
  ActivityInfo,
  DiscoveryCandidate,
  DiscoveryPromptResult,
  DiscoveryResult,
  FilterState,
  InvestorDatabasePayload,
  InvestorDatabaseRow,
  InvestorField,
  InvestorRecord,
  ListColumn,
  Person,
  SpreadsheetRow,
} from "../types";

type RecordInput = Partial<Record<keyof InvestorRecord, unknown>> & Record<string, unknown>;

export function normalizeRecord(input: RecordInput = {}): InvestorRecord {
  const record: InvestorRecord = {
    id: "",
    firm: "",
    contact: "",
    type: "",
    linkNotes: "",
    status: "",
    introSource: "",
    dateContacted: "",
    nextStep: "",
    followUpDate: "",
    notes: "",
    checkSize: "",
    round: "",
    priority: "",
    aiFitScore: "",
    people: [],
    aiResearch: "",
    aiResearchSources: "",
    aiResearchedAt: "",
    discoveryKey: "",
    discoveryFoundAt: "",
    discoveryQuery: "",
    discoveryReason: "",
    name: "",
    url: "",
  };

  for (const { key } of fieldDefinitions) {
    if (key === "people") {
      record.people = normalizePeople(input[key]);
    } else {
      record[key] = String(input[key] || "").trim();
    }
  }

  record.id = String(input.id || `custom-${randomId()}`);
  record.name = buildName(record);
  record.url = firstUrl(record.linkNotes);
  record.status = normalizeStatus(record.status);
  record.discoveryKey = record.discoveryKey || buildDiscoveryKey(record);
  return record;
}

export function fromDatabaseRecord(row: InvestorDatabaseRow): InvestorRecord {
  return normalizeRecord({
    id: row.id,
    firm: row.firm,
    contact: row.contact,
    type: row.investor_type,
    linkNotes: row.link_notes,
    status: row.status,
    introSource: row.intro_source,
    dateContacted: row.date_contacted,
    nextStep: row.next_step,
    followUpDate: row.follow_up_date,
    notes: row.notes,
    checkSize: row.check_size,
    round: row.funding_round,
    priority: row.priority,
    aiFitScore: row.ai_fit_score,
    people: row.people,
    aiResearch: row.ai_research,
    aiResearchSources: row.ai_research_sources,
    aiResearchedAt: row.ai_researched_at,
    discoveryKey: row.discovery_key,
    discoveryFoundAt: row.discovery_found_at,
    discoveryQuery: row.discovery_query,
    discoveryReason: row.discovery_reason,
  });
}

export function toDatabaseRecord(record: InvestorRecord): InvestorDatabasePayload {
  return {
    id: record.id,
    firm: record.firm,
    contact: record.contact,
    investor_type: record.type,
    link_notes: record.linkNotes,
    status: record.status,
    intro_source: record.introSource,
    date_contacted: record.dateContacted || null,
    next_step: record.nextStep,
    follow_up_date: record.followUpDate || null,
    notes: record.notes,
    check_size: record.checkSize,
    funding_round: record.round,
    priority: record.priority,
    ai_fit_score: clampInteger(record.aiFitScore, 1, 10),
    people: cleanPeople(record.people),
    ai_research: record.aiResearch,
    ai_research_sources: record.aiResearchSources,
    ai_researched_at: record.aiResearchedAt || null,
    discovery_key: record.discoveryKey,
    discovery_found_at: record.discoveryFoundAt || null,
    discovery_query: record.discoveryQuery,
    discovery_reason: record.discoveryReason,
  };
}

export function validateMappedFieldsStayCanonical(): boolean {
  return fieldDefinitions.every(({ key }) => {
    if (key === "people") return dbFields.people === "people";
    return key in dbFields || jsonFields.has(key) || integerFields.has(key) || nullableFields.has(key);
  });
}

export function normalizeStatus(value: unknown): string {
  const status = String(value || "").trim();
  if (status === "Passed") return "VC Passed";
  return status || "Not Started";
}

export function buildName(record: Pick<InvestorRecord, "firm" | "contact">): string {
  if (record.firm && record.contact && record.firm.toLowerCase() !== record.contact.toLowerCase()) {
    return `${record.firm} - ${record.contact}`;
  }
  return record.contact || record.firm || "Untitled";
}

export function firstUrl(value: unknown): string {
  const match = String(value || "").match(/https?:\/\/[^\s,)]+/);
  return match ? match[0] : "";
}

export function normalizePeople(value: unknown): Person[] {
  let people = value;
  if (typeof people === "string") {
    try {
      people = JSON.parse(people);
    } catch {
      people = [];
    }
  }
  if (!Array.isArray(people)) return [];

  return people.map((person) => ({
    name: String(person?.name || "").trim(),
    title: String(person?.title || "").trim(),
    linkedin: String(person?.linkedin || "").trim(),
    xProfile: String(person?.xProfile || "").trim(),
    email: String(person?.email || "").trim(),
    notes: String(person?.notes || "").trim(),
  }));
}

export function hasPersonContent(person: Person): boolean {
  return personFields.some((field) => person[field]);
}

export function cleanPeople(value: unknown): Person[] {
  return normalizePeople(value).filter(hasPersonContent);
}

export function peopleSearchText(value: unknown): string {
  return cleanPeople(value)
    .map((person) => personFields.map((field) => person[field]).join(" "))
    .join(" ");
}

export function fieldText(record: InvestorRecord, key: InvestorField): string {
  if (key === "people") return peopleSearchText(record.people);
  return record[key] || "";
}

export function fieldExportValue(record: InvestorRecord, key: InvestorField): string {
  if (key === "people") {
    return cleanPeople(record.people)
      .map((person) =>
        [
          person.name,
          person.title,
          person.email,
          person.linkedin,
          person.xProfile,
          person.notes,
        ]
          .filter(Boolean)
          .join(" | "),
      )
      .join("\n");
  }
  return record[key] || "";
}

export function normalizeListColumnKeys(keys: unknown): string[] {
  const selected = new Set(Array.isArray(keys) ? keys.map(String) : []);
  const requiredKeys = listColumns.filter((column) => column.required).map((column) => column.key);

  for (const key of requiredKeys) {
    selected.add(key);
  }

  const normalized = listColumns
    .filter((column) => selected.has(column.key))
    .map((column) => column.key);

  return normalized.length ? normalized : [...defaultListColumnKeys];
}

export function getActiveListColumns(keys: string[]): ListColumn[] {
  const selected = new Set(keys);
  const columns = listColumns.filter((column) => selected.has(column.key));
  return columns.length ? columns : listColumns.filter((column) => defaultListColumnKeys.includes(column.key));
}

export function gridStyleForColumns(columns: ListColumn[]): CSSProperties {
  const template = columns.map((column) => column.width).join(" ");
  const minWidth = columns.reduce((total, column) => total + column.minWidth, 0);
  const gaps = Math.max(columns.length - 1, 0) * 12;
  const horizontalPadding = 36;
  return {
    gridTemplateColumns: template,
    minWidth: `${minWidth + gaps + horizontalPadding}px`,
  };
}

export function statusValues(records: InvestorRecord[]): string[] {
  const values = [...new Set([...statusOrder, ...records.map((record) => record.status).filter(Boolean)])];
  return values.sort((a, b) => statusRank(a) - statusRank(b) || a.localeCompare(b));
}

export function uniqueValues(records: InvestorRecord[], field: keyof InvestorRecord, preferredOrder: string[] = []): string[] {
  const values = [...new Set(records.map((record) => record[field]).filter((value): value is string => typeof value === "string" && Boolean(value)))];
  return values.sort((a, b) => {
    const aIndex = preferredOrder.indexOf(a);
    const bIndex = preferredOrder.indexOf(b);
    if (aIndex >= 0 || bIndex >= 0) {
      return (aIndex >= 0 ? aIndex : 99) - (bIndex >= 0 ? bIndex : 99);
    }
    return a.localeCompare(b);
  });
}

export function filterRecords(records: InvestorRecord[], filters: FilterState): InvestorRecord[] {
  const query = filters.query.trim().toLowerCase();
  return records
    .filter((record) => {
      const haystack = fieldDefinitions.map(({ key }) => fieldText(record, key)).join(" ").toLowerCase();
      const matchesQuery = !query || haystack.includes(query);
      const matchesStatus =
        filters.status === "all" ||
        (filters.status === "visible" && !hiddenStatuses.has(record.status)) ||
        record.status === filters.status;
      const matchesType = filters.type === "all" || record.type === filters.type;
      const matchesPriority = filters.priority === "all" || record.priority === filters.priority;
      const matchesFollowUpDate = !filters.followUpDate || record.followUpDate === filters.followUpDate;
      return matchesQuery && matchesStatus && matchesType && matchesPriority && matchesFollowUpDate;
    })
    .sort((a, b) => sortRecords(a, b, filters));
}

export function sortRecords(a: InvestorRecord, b: InvestorRecord, filters: FilterState): number {
  if (filters.sort === "name") {
    return a.name.localeCompare(b.name);
  }

  if (filters.sort === "status") {
    return statusRank(a.status) - statusRank(b.status) || a.name.localeCompare(b.name);
  }

  if (filters.sort === "priority") {
    return (
      (priorityScore[b.priority] || 0) -
        (priorityScore[a.priority] || 0) ||
      activityTime(b) -
        activityTime(a) ||
      a.name.localeCompare(b.name)
    );
  }

  if (filters.sort === "followup") {
    return followUpTime(a) - followUpTime(b) || a.name.localeCompare(b.name);
  }

  return activityTime(b) - activityTime(a) || statusRank(a.status) - statusRank(b.status) || a.name.localeCompare(b.name);
}

export function statusRank(status: string): number {
  const index = statusOrder.indexOf(status);
  return index >= 0 ? index : 99;
}

export function followUpTime(record: InvestorRecord): number {
  return parseDate(record.followUpDate)?.getTime() || Number.MAX_SAFE_INTEGER;
}

export function activityTime(record: InvestorRecord): number {
  return parseDate(record.dateContacted)?.getTime() || 0;
}

export function startOfToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

export function lastActivity(record: InvestorRecord, today = startOfToday()): ActivityInfo {
  const contacted = parseDate(record.dateContacted);
  if (contacted) {
    return {
      label: `Contacted ${formatShortDate(contacted)}`,
      description: `Last activity: contacted ${formatLongDate(contacted)}${relativeDate(contacted, today)}`,
    };
  }

  if (record.status && record.status !== "Not Started") {
    return {
      label: record.status,
      description: `Last activity: ${record.status.toLowerCase()} with no date recorded`,
    };
  }

  if (record.nextStep) {
    return {
      label: "Next step set",
      description: "Last activity: next step captured",
    };
  }

  return {
    label: "No activity",
    description: "Last activity: none recorded yet",
  };
}

export function isFollowUpDue(record: InvestorRecord, today = startOfToday()): boolean {
  const followUp = parseDate(record.followUpDate);
  return Boolean(followUp && followUp <= today);
}

export function parseDate(value: unknown): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function dateValue(value: unknown): string {
  return parseDate(value) ? String(value) : "";
}

export function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function formatShortDate(date: Date): string {
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(date);
}

export function formatLongDate(date: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

export function followUpLabel(record: InvestorRecord, today = startOfToday()): string {
  const followUp = parseDate(record.followUpDate);
  if (!followUp) return "No follow-up";
  return `${isFollowUpDue(record, today) ? "Due" : "Follow-up"} ${formatShortDate(followUp)}`;
}

export function researchListLabel(value: unknown): string {
  const date = new Date(String(value || ""));
  if (Number.isNaN(date.getTime())) return "Not researched";
  return `Updated ${formatShortDate(date)}`;
}

export function researchUpdatedLabel(value: unknown): string {
  const date = new Date(String(value || ""));
  if (Number.isNaN(date.getTime())) return "Not researched yet";
  return `Updated ${new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date)}`;
}

export function clampInteger(value: unknown, min: number, max: number): number | null {
  if (String(value || "").trim() === "") return null;
  const number = Number.parseInt(String(value), 10);
  if (Number.isNaN(number)) return null;
  return Math.min(max, Math.max(min, number));
}

export function fitScoreLabel(value: unknown): string {
  const score = clampInteger(value, 1, 10);
  return score ? `Fit ${score}/10` : "";
}

export function compactMeta(record: InvestorRecord): string {
  const fitScore = fitScoreLabel(record.aiFitScore);
  const people = cleanPeople(record.people);
  const peopleLabel = people.length ? `${people.length} people` : "";
  return [record.type, record.priority, record.checkSize, fitScore, peopleLabel].filter(Boolean).join(" / ") || "No type set";
}

export function listColumnValue(record: InvestorRecord, key: ListColumn["key"], today = startOfToday()): string {
  if (key === "type") return record.type || "Unspecified";
  if (key === "priority") return record.priority || "Unspecified";
  if (key === "checkSize") return record.checkSize || "Unspecified";
  if (key === "round") return record.round || "Unspecified";
  if (key === "fitScore") return fitScoreLabel(record.aiFitScore) || "No score";
  if (key === "people") {
    const count = cleanPeople(record.people).length;
    if (count === 1) return "1 person";
    return count ? `${count} people` : "No people";
  }
  if (key === "followUp") return followUpLabel(record, today);
  if (key === "source") return record.introSource || "No source";
  if (key === "research") return researchListLabel(record.aiResearchedAt);
  return "";
}

export function statusClass(status: string): string {
  return `status-${slug(status || "unspecified")}`;
}

export function slug(value: unknown): string {
  return String(value).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

export function buildDiscoveryKey(record: Partial<InvestorRecord>): string {
  const identity = recordIdentity(record);
  const domains = [...identity.domains].sort();
  const linkedins = [...identity.linkedins].sort();
  const emails = [...identity.emails].sort();
  return [
    identity.pair ? `pair:${identity.pair}` : "",
    domains[0] ? `domain:${domains[0]}` : "",
    linkedins[0] ? `linkedin:${linkedins[0]}` : "",
    emails[0] ? `email:${emails[0]}` : "",
  ].filter(Boolean).join("|");
}

export function recordIdentity(record: Partial<InvestorRecord>) {
  const firm = normalizeIdentity(record.firm);
  const contact = normalizeIdentity(record.contact);
  const urls = [
    ...extractUrls(record.linkNotes),
    ...extractUrls(record.aiResearchSources),
    ...cleanPeople(record.people).flatMap((person) => [person.linkedin, person.xProfile]),
  ];
  const linkedins = new Set(urls.map(normalizeUrl).filter((url) => url.includes("linkedin.com/")));
  const domains = new Set(
    urls
      .map(primaryDomain)
      .filter((domain) => domain && !["linkedin.com", "twitter.com", "x.com"].includes(domain)),
  );
  const emails = new Set(cleanPeople(record.people).map((person) => firstEmail(person.email).toLowerCase()).filter(Boolean));

  return {
    key: record.discoveryKey || "",
    firm,
    contact,
    pair: firm || contact ? `${firm}|${contact}` : "",
    domains,
    linkedins,
    emails,
  };
}

export function normalizeIdentity(value: unknown): string {
  return String(value || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

export function normalizeUrl(value: unknown): string {
  const url = safeUrl(value);
  if (!url) return "";
  try {
    const parsed = new URL(url);
    parsed.hash = "";
    parsed.search = "";
    return parsed.href.replace(/\/$/, "").toLowerCase();
  } catch {
    return "";
  }
}

export function primaryDomain(value: unknown): string {
  const url = safeUrl(value);
  if (!url) return "";
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return "";
  }
}

export function extractUrls(value: unknown): string[] {
  return String(value || "").match(/https?:\/\/[^\s,)]+/g) || [];
}

export function normalizeSourceLines(value: unknown): string[] {
  if (Array.isArray(value)) {
    return [...new Set(value.map((item) => String(item || "").trim()).filter(Boolean))];
  }
  return String(value || "")
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function mergeLineValues(a: unknown, b: unknown): string {
  return joinKnownUnique([...normalizeSourceLines(a), ...normalizeSourceLines(b)]).join("\n");
}

export function parseInvestorCsv(text: string): SpreadsheetRow[] {
  const rows = parseCsvRows(text).filter((row) => row.some((cell) => String(cell || "").trim()));
  if (rows.length < 2) return [];

  return rows
    .slice(1)
    .map((row) => ({
      firm: String(row[0] || "").trim(),
      lastFundSize: String(row[1] || "").trim(),
      activeInvestments: String(row[2] || "").trim(),
      location: String(row[3] || "").trim(),
      sectors: String(row[4] || "").trim(),
      leadRounds: String(row[5] || "").trim(),
      stage: String(row[6] || "").trim(),
      checkSize: String(row[7] || "").trim(),
      url: String(row[8] || "").trim(),
    }))
    .filter((row) => row.firm);
}

export function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];

    if (char === "\"") {
      if (inQuotes && next === "\"") {
        cell += "\"";
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      row.push(cell);
      cell = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && next === "\n") index += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
      continue;
    }

    cell += char;
  }

  if (cell || row.length) {
    row.push(cell);
    rows.push(row);
  }

  return rows;
}

export function uniqueSpreadsheetRows(rows: SpreadsheetRow[]): SpreadsheetRow[] {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const record = spreadsheetSeedRecord(row);
    const key = buildDiscoveryKey(record) || normalizeIdentity(row.firm);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function spreadsheetSeedRecord(row: SpreadsheetRow): InvestorRecord {
  return normalizeRecord({
    firm: row.firm,
    contact: "",
    type: "VC",
    linkNotes: row.url,
    checkSize: row.checkSize,
    round: row.stage,
    introSource: "Spreadsheet Import",
  });
}

export function spreadsheetSeedSummary(row: SpreadsheetRow): string {
  return [
    `Fund: ${row.firm}`,
    row.lastFundSize ? `Last fund size: ${row.lastFundSize}` : "",
    row.activeInvestments ? `Making active investments: ${row.activeInvestments}` : "",
    row.location ? `Location: ${row.location}` : "",
    row.sectors ? `Sectors: ${row.sectors}` : "",
    row.leadRounds ? `Leads rounds: ${row.leadRounds}` : "",
    row.stage ? `Stage: ${row.stage}` : "",
    row.checkSize ? `Average initial check size: ${row.checkSize}` : "",
    row.url ? `URL: ${row.url}` : "",
  ].filter(Boolean).join("; ");
}

export function normalizeDiscoveryCandidate(candidate: DiscoveryCandidate, query: string, foundAt: string): InvestorRecord {
  const fitScore = clampInteger(candidate.fitScore, 1, 10);
  const status = fitScore && fitScore >= DISCOVERY_MIN_SCORE ? "Researching" : "Filtered Out";
  const sources = normalizeSourceLines(candidate.sources);
  const record = normalizeRecord({
    id: `discovery-${Date.now()}-${slug([candidate.firm, candidate.contact].filter(Boolean).join("-") || randomId())}`,
    firm: candidate.firm,
    contact: candidate.contact || "",
    type: candidate.type || candidate.investorType || "VC",
    linkNotes: joinKnownUnique([candidate.linkNotes, candidate.website, ...sources]).join("\n"),
    status,
    introSource: "AI Discovery",
    nextStep: candidate.nextStep || "",
    notes: candidate.notes || "",
    checkSize: candidate.checkSize || "",
    round: candidate.round || candidate.stage || "",
    priority: candidate.priority || priorityForFitScore(fitScore),
    aiFitScore: fitScore ? String(fitScore) : "",
    people: normalizePeople(candidate.people),
    aiResearch: candidate.summary || candidate.aiResearch || "",
    aiResearchSources: sources.join("\n"),
    aiResearchedAt: foundAt,
    discoveryFoundAt: foundAt,
    discoveryQuery: query,
    discoveryReason: candidate.discoveryReason || candidate.reason || candidate.fitScoreReason || "",
  });
  record.discoveryKey = candidate.discoveryKey || buildDiscoveryKey(record);
  if (!record.aiResearch) {
    record.aiResearch = joinKnownUnique([
      candidate.fit,
      candidate.fitScoreReason,
      candidate.decisionMakers,
      candidate.discoveryReason || candidate.reason,
    ]).join("\n\n");
  }
  return record;
}

export function priorityForFitScore(score: number | null): string {
  if ((score || 0) >= 8) return "High";
  if ((score || 0) >= DISCOVERY_MIN_SCORE) return "Medium";
  return "Low";
}

export function discoveryResult(record: InvestorRecord, outcome: string, candidate: DiscoveryCandidate | InvestorRecord): DiscoveryResult {
  return {
    id: record.id,
    name: record.name,
    status: record.status,
    outcome,
    fitScore: record.aiFitScore,
    reason:
      ("discoveryReason" in candidate ? candidate.discoveryReason : "") ||
      ("fitScoreReason" in candidate ? candidate.fitScoreReason : "") ||
      record.discoveryReason ||
      record.nextStep ||
      "",
  };
}

export function discoveryResultForPrompt(result: DiscoveryResult): DiscoveryPromptResult {
  return {
    name: result.name,
    outcome: result.outcome,
    status: result.status,
    fitScore: result.fitScore,
    reason: result.reason,
  };
}

export function mergeDiscoveryResults(existingResults: DiscoveryResult[], nextResults: DiscoveryResult[]): DiscoveryResult[] {
  const merged = new Map<string, DiscoveryResult>();
  for (const result of [...existingResults, ...nextResults]) {
    const key = result.id || result.name;
    const previous = merged.get(key);
    if (!previous || discoveryOutcomeRank(result) < discoveryOutcomeRank(previous)) {
      merged.set(key, result);
    }
  }
  return [...merged.values()];
}

export function sortDiscoveryResults(results: DiscoveryResult[]): DiscoveryResult[] {
  return [...results].sort((a, b) =>
    discoveryOutcomeRank(a) - discoveryOutcomeRank(b) ||
    (clampInteger(b.fitScore, 1, 10) || 0) - (clampInteger(a.fitScore, 1, 10) || 0) ||
    a.name.localeCompare(b.name),
  );
}

export function discoveryOutcomeRank(result: DiscoveryResult): number {
  if (result.outcome === "Added") return 0;
  if (result.outcome === "Filtered out") return 1;
  return 3;
}

export function newDiscoveryCount(results: DiscoveryResult[]): number {
  return results.filter((result) => result.outcome === "Added" || result.outcome === "Filtered out").length;
}

export function discoverySummaryMessage(results: DiscoveryResult[], skippedCount = 0): string {
  const added = results.filter((result) => result.outcome === "Added").length;
  const filtered = results.filter((result) => result.outcome === "Filtered out").length;
  const newCount = added + filtered;
  const newLabel = `${newCount} new`;
  const activeDetail = `${added} active, ${filtered} filtered`;
  const skippedDetail = skippedCount ? `, skipped ${skippedCount} existing` : "";

  if (newCount) {
    return `Discovery saved ${newLabel} (${activeDetail})${skippedDetail}`;
  }
  return skippedCount ? `Discovery skipped ${skippedCount} existing; no new investors found` : "Discovery found no new investors";
}

export function spreadsheetSummaryMessage(summary: {
  saved: number;
  skippedExisting: number;
  skippedUnusable: number;
  errors: number;
}): string {
  const pieces = [`Spreadsheet saved ${summary.saved} researched ${summary.saved === 1 ? "fund" : "funds"}`];
  if (summary.skippedExisting) pieces.push(`skipped ${summary.skippedExisting} existing`);
  if (summary.skippedUnusable) pieces.push(`skipped ${summary.skippedUnusable} unusable`);
  if (summary.errors) pieces.push(`${summary.errors} batch ${summary.errors === 1 ? "error" : "errors"}`);
  return pieces.join(", ");
}

export function findDuplicateRecord(records: InvestorRecord[], candidate: Partial<InvestorRecord>): InvestorRecord | undefined {
  const candidateIdentity = recordIdentity(candidate);
  return records.find((record) => {
    const identity = recordIdentity(record);
    if (candidateIdentity.key && identity.key && candidateIdentity.key === identity.key) return true;
    if (candidateIdentity.firm && identity.firm && candidateIdentity.firm === identity.firm) return true;
    if (candidateIdentity.pair && identity.pair && candidateIdentity.pair === identity.pair) return true;
    if (setsOverlap(candidateIdentity.domains, identity.domains)) return true;
    if (setsOverlap(candidateIdentity.linkedins, identity.linkedins)) return true;
    if (setsOverlap(candidateIdentity.emails, identity.emails)) return true;
    return false;
  });
}

export function discoveryExclusionRecords(records: InvestorRecord[]) {
  return records.map((record) => ({
    name: record.name,
    firm: record.firm,
    contact: record.contact,
    type: record.type,
    status: record.status,
    fitScore: record.aiFitScore,
    discoveryKey: record.discoveryKey || buildDiscoveryKey(record),
    domains: [...recordIdentity(record).domains],
    linkedins: [...recordIdentity(record).linkedins],
    people: cleanPeople(record.people).map((person) => ({
      name: person.name,
      title: person.title,
      linkedin: person.linkedin,
      email: firstEmail(person.email),
    })),
  }));
}

export function buildSignalSummary(records: InvestorRecord[]) {
  const positiveStatuses = new Set(["Meeting Scheduled", "In Talks", "Diligence", "Committed", "Closed"]);
  const negativeStatuses = new Set(["VC Passed", "Not Pursuing", "Filtered Out"]);
  const neutralStatuses = new Set(["Researching", "Need Intro", "Contacted", "Applied"]);

  return {
    positive: signalRecords(records, (record) =>
      (clampInteger(record.aiFitScore, 1, 10) || 0) >= 8 ||
      positiveStatuses.has(record.status) ||
      record.priority === "High",
    ),
    negative: signalRecords(records, (record) =>
      negativeStatuses.has(record.status) ||
      Boolean(record.aiFitScore && (clampInteger(record.aiFitScore, 1, 10) || 0) <= 5),
    ),
    neutral: signalRecords(records, (record) => neutralStatuses.has(record.status)),
    guidance: "Use positive examples to find similar stage, thesis, sector, check size, partner background, and portfolio overlap. Avoid negative patterns. Return roughly 8 close-fit candidates and 2 adjacent but plausible candidates.",
  };
}

export function signalRecords(records: InvestorRecord[], predicate: (record: InvestorRecord) => boolean) {
  return records
    .filter(predicate)
    .sort((a, b) => signalRank(b) - signalRank(a) || a.name.localeCompare(b.name))
    .slice(0, 20)
    .map((record) => ({
      name: record.name,
      type: record.type,
      status: record.status,
      priority: record.priority,
      fitScore: record.aiFitScore,
      checkSize: record.checkSize,
      round: record.round,
      notes: [record.aiResearch, record.notes, record.discoveryReason].filter(Boolean).join(" ").slice(0, 500),
    }));
}

export function signalRank(record: InvestorRecord): number {
  const score = clampInteger(record.aiFitScore, 1, 10) || 0;
  const positiveStatusBoost = ["Meeting Scheduled", "In Talks", "Diligence", "Committed", "Closed"].includes(record.status) ? 12 : 0;
  const negativeStatusBoost = ["VC Passed", "Not Pursuing", "Filtered Out"].includes(record.status) ? 12 : 0;
  const priorityBoost = record.priority === "High" ? 8 : record.priority === "Medium" ? 4 : 0;
  const researchBoost = record.aiResearch || record.notes || record.discoveryReason ? 2 : 0;
  return score + positiveStatusBoost + negativeStatusBoost + priorityBoost + researchBoost;
}

export function isUnreviewedRecord(record: InvestorRecord): boolean {
  return !(
    record.aiResearchedAt ||
    record.aiResearch ||
    record.aiResearchSources ||
    record.aiFitScore
  );
}

export function listSummaryLabel(records: InvestorRecord[], visibleCount: number, status: string): string {
  const hiddenCount = records.filter((record) => hiddenStatuses.has(record.status)).length;
  if (status === "visible" && hiddenCount) {
    return `${visibleCount} visible contacts / ${hiddenCount} filtered out`;
  }
  return `${visibleCount} of ${records.length} contacts`;
}

export function pipelineMetrics(records: InvestorRecord[], today = startOfToday()) {
  const pipelineRecords = records.filter((record) => !hiddenStatuses.has(record.status));
  return [
    ["Total", pipelineRecords.length],
    ["Active", pipelineRecords.filter((record) => record.status && !inactiveStatuses.has(record.status)).length],
    ["Contacted", pipelineRecords.filter((record) => record.status === "Contacted").length],
    ["High priority", pipelineRecords.filter((record) => record.priority === "High").length],
    ["Follow-ups due", pipelineRecords.filter((record) => isFollowUpDue(record, today)).length],
  ] as const;
}

export function exportRecordsCsv(records: InvestorRecord[], today = startOfToday()): { filename: string; csv: string } {
  const header = fieldDefinitions.map(({ label }) => label);
  const rows = records.map((record) => fieldDefinitions.map(({ key }) => fieldExportValue(record, key)));
  return {
    filename: `fundraising-crm-${toIsoDate(today)}.csv`,
    csv: [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\n"),
  };
}

export function csvCell(value: unknown): string {
  let raw = String(value);
  if (/^[=+\-@\t\r]/.test(raw)) {
    raw = `'${raw}`;
  }
  return `"${raw.replace(/"/g, '""')}"`;
}

export function safeUrl(value: unknown): string {
  try {
    const url = new URL(String(value || ""));
    return ["http:", "https:"].includes(url.protocol) ? url.href : "";
  } catch {
    return "";
  }
}

export function firstEmail(value: unknown): string {
  const match = String(value || "").match(/[^\s,;<>]+@[^\s,;<>]+\.[^\s,;<>]+/);
  return match ? match[0] : "";
}

export function joinKnownUnique(values: unknown[]): string[] {
  return [...new Set(values.map((value) => String(value || "").trim()).filter(Boolean))];
}

export function setsOverlap<T>(a: Set<T>, b: Set<T>): boolean {
  for (const value of a) {
    if (b.has(value)) return true;
  }
  return false;
}

export function relativeDate(date: Date, today = startOfToday()): string {
  const days = Math.round((today.getTime() - date.getTime()) / 86400000);
  if (days === 0) return " (today)";
  if (days === 1) return " (1 day ago)";
  if (days > 1) return ` (${days} days ago)`;
  if (days === -1) return " (tomorrow)";
  return ` (in ${Math.abs(days)} days)`;
}

export function recordPatchWithDerivedFields(
  record: InvestorRecord,
  changes: Partial<InvestorRecord>,
): InvestorRecord {
  const next = { ...record };
  for (const [field, value] of Object.entries(changes) as Array<[keyof InvestorRecord, unknown]>) {
    if (field === "people") {
      next.people = normalizePeople(value);
    } else if (typeof value === "string") {
      next[field] = value.trimStart() as never;
    } else {
      next[field] = value as never;
    }
  }
  if ("status" in changes) {
    next.status = normalizeStatus(next.status);
  }
  next.name = buildName(next);
  next.url = firstUrl(next.linkNotes);
  if (!next.discoveryKey || ["firm", "contact", "linkNotes", "people", "aiResearchSources"].some((field) => field in changes)) {
    next.discoveryKey = buildDiscoveryKey(next);
  }
  return next;
}

export function randomId(): string {
  return globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
}
